import JSZip from "jszip";
import { saveAs } from "file-saver";

/**
 * Sanitize a string for safe use as a filename component
 * @param {string} value
 * @returns {string}
 */
const sanitizeForFilename = (value) => {
    return String(value || "")
        .trim()
        .replace(/[^a-zA-Z0-9 _-]/g, "")
        .replace(/\s+/g, "_");
};

/**
 * Derive a file extension from a (possibly signed) URL, defaulting to .jpg
 * @param {string} url
 * @returns {string}
 */
const getExtensionFromUrl = (url) => {
    try {
        const pathname = new URL(url).pathname;
        const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
        return match ? `.${match[1].toLowerCase()}` : ".jpg";
    } catch (error) {
        return ".jpg";
    }
};

/**
 * Build a filesystem-safe filename for a photo
 * @param {Object} photo - Photo object with photo_name, sub_tag, original_url
 * @param {Object} vehicle - Vehicle object with stock
 * @returns {string}
 */
export const buildPhotoFilename = (photo, vehicle) => {
    const parts = [
        sanitizeForFilename(vehicle?.stock),
        sanitizeForFilename(photo.photo_name),
        photo.sub_tag ? sanitizeForFilename(photo.sub_tag) : null
    ].filter(Boolean);

    const extension = getExtensionFromUrl(photo.original_url);
    return `${parts.join("_")}${extension}`;
};

/**
 * Download a single existing photo at full resolution
 * @param {Object} photo - Photo object with original_url
 * @param {Object} vehicle - Vehicle object with stock
 * @returns {Promise<void>}
 */
export const downloadSinglePhoto = async (photo, vehicle) => {
    const response = await fetch(photo.original_url);
    if (!response.ok) {
        throw new Error(`Failed to fetch photo (status ${response.status})`);
    }
    const blob = await response.blob();
    const filename = buildPhotoFilename(photo, vehicle);
    saveAs(blob, filename);
};

/**
 * Download all existing photos for a vehicle bundled as a single zip
 * @param {Array} photos - Array of photo objects with original_url
 * @param {Object} vehicle - Vehicle object with stock, year, make, model
 * @returns {Promise<{ successCount: number, failures: Array<{ photo: Object, error: string }> }>}
 */
export const downloadAllPhotosAsZip = async (photos, vehicle) => {
    if (!photos || photos.length === 0) {
        return { successCount: 0, failures: [] };
    }

    const zip = new JSZip();
    const usedNames = new Map();
    const failures = [];

    for (const photo of photos) {
        try {
            const response = await fetch(photo.original_url);
            if (!response.ok) {
                throw new Error(`status ${response.status}`);
            }
            const blob = await response.blob();

            let filename = buildPhotoFilename(photo, vehicle);
            if (usedNames.has(filename)) {
                const count = usedNames.get(filename) + 1;
                usedNames.set(filename, count);
                const dotIndex = filename.lastIndexOf(".");
                filename = dotIndex !== -1
                    ? `${filename.slice(0, dotIndex)}_${count}${filename.slice(dotIndex)}`
                    : `${filename}_${count}`;
            } else {
                usedNames.set(filename, 1);
            }

            zip.file(filename, blob);
        } catch (error) {
            failures.push({ photo, error: error.message });
        }
    }

    if (Object.keys(zip.files).length === 0) {
        throw new Error("Failed to download any photos");
    }

    const content = await zip.generateAsync({ type: "blob" });
    const zipName = [
        sanitizeForFilename(vehicle?.stock),
        sanitizeForFilename(vehicle?.year),
        sanitizeForFilename(vehicle?.make),
        sanitizeForFilename(vehicle?.model)
    ].filter(Boolean).join("_");
    saveAs(content, `${zipName || "vehicle"}_photos.zip`);

    return { successCount: photos.length - failures.length, failures };
};
